import { Modal } from 'antd';
import { translate } from '../../lib/lang.helper';
import { useDispatch, useSelector } from 'react-redux';
import { selectLang } from '../../store/ui/ui.selector';
import { selectAccount } from '../../store/auth/auth.selector';
import { CheckboxButton } from '../shared';
import { useState } from 'react';
import { updateAccountSetting } from '../../store/auth/auth.actions';
import { TLang } from '../../entities/ui.entities';

const baseColumnKeys = [
  'line_name',
  'crop_type',
  'length',
  'date_seeded',
  'planned_date',
  'seed_type',
  'last_assessment_date',
  'avg_size',
  'line_group',
  'spat_storage',
  'days_growth',
  'inventory',
  'fill_percent',
  'est_amount',
  'photo',
  'comment',
];

const baseColumnMap = {
  line_name: 'Line',
  crop_type: 'Crop type',
  length: 'Length/Containers',
  date_seeded: 'Date seeded',
  planned_date: 'Planned harvest date',
  seed_type: 'Seed type',
  last_assessment_date: 'Last assessment date',
  avg_size: 'Avg. Size',
  line_group: 'Line group',
  spat_storage: 'Batch seeded',
  days_growth: 'Days growth',
  inventory: 'Inventory',
  fill_percent: 'Line fill percentage',
  est_amount: 'Est amount',
  photo: 'Photo',
  comment: 'Comment',
};

export const getLineColumnLabel = (
  key: string,
  lang: TLang | undefined = undefined,
) => {
  if (key in baseColumnMap) {
    return translate(lang, baseColumnMap[key as keyof typeof baseColumnMap]);
  }
  return translate(lang, key);
};

interface Props {
  visible: boolean;
  onClose: () => void;
}

const LineColumnModifyModal = ({ visible, onClose }: Props) => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);
  const accSetting = useSelector(selectAccount);
  const hiddenColumns = accSetting?.line_hidden_columns ?? [];
  const columnsOrder = accSetting?.line_columns_order ?? [];

  const initialColumns = baseColumnKeys.sort(
    (a, b) => columnsOrder.indexOf(a) - columnsOrder.indexOf(b),
  );

  const [checked, setChecked] = useState(hiddenColumns);

  const [tableColumns, setTableColumns] = useState(initialColumns);

  const toggleCheck = (name: string, c: boolean) => {
    if (name === 'Line') return;
    if (c) {
      setChecked(checked.filter(x => x !== name));
    } else if (!checked.includes(name)) {
      setChecked([...checked, name]);
    }
  };
  const confirmClick = async () => {
    const columns = tableColumns.filter(x => checked.includes(x));
    let data = {
      line_hidden_columns: columns,
      line_columns_order: tableColumns,
    };
    const r = await dispatch(updateAccountSetting(data));
    if (r !== true) {
      window.alert(r ?? 'Server error');
    } else {
      onClose();
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (index: number) => {
    return (e: React.DragEvent) => {
      const draggedIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
      if (draggedIndex === index) return;
      const newOrder = [...tableColumns];
      const [draggedItem] = newOrder.splice(draggedIndex, 1);
      newOrder.splice(index, 0, draggedItem);
      setTableColumns(newOrder);
    };
  };

  const handleDragStart = (index: number) => {
    return (e: React.DragEvent) => {
      e.dataTransfer.setData('text/plain', String(index));
    };
  };

  return (
    <Modal
      title={translate(lang, 'Modify columns')}
      visible={visible}
      onCancel={onClose}
      onOk={confirmClick}
      closable
    >
      <div className='wrap pl-12 pr-12'>
        {tableColumns.map((x, i) => (
          <div
            key={i}
            draggable
            onDragStart={handleDragStart(i)}
            onDragOver={handleDragOver}
            onDrop={handleDrop(i)}
            className='mb-17'
          >
            <CheckboxButton
              label={getLineColumnLabel(x, lang)}
              checked={!checked.includes(x)}
              disabled={x === 'line_name'}
              onChange={e => toggleCheck(x, e.target.checked)}
            />
          </div>
        ))}
      </div>
    </Modal>
  );
};

export default LineColumnModifyModal;
